import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../context/Language/LanguageContext";
import { isAllowedEmailUser } from "../utils/access";
import {
    needsMigration,
    runMigration,
} from "../utils/firestoreMigration";

// Phase -> human label key (translated via t()).
const PHASE_LABELS = {
  matches: "Migrating matches",
  teams: "Migrating teams & players",
  tournaments: "Migrating tournaments",
  finalizing: "Finishing up",
};

// Rough overall weighting so the bar advances smoothly across phases.
const PHASE_WEIGHT = { matches: 0.8, teams: 0.15, tournaments: 0.05 };
const PHASE_BASE = { matches: 0, teams: 0.8, tournaments: 0.95, finalizing: 1 };

/**
 * Runs the one-time Firestore migration the first time a user logs in and shows
 * a progress overlay while it works.  Renders nothing once the user has been
 * migrated (or while the check is pending).
 */
export default function MigrationGate({ firebaseUser }) {
  const { t } = useLanguage();
  const startedRef = useRef(false);
  const [status, setStatus] = useState("idle"); // idle | running | error | done
  const [progress, setProgress] = useState({
    phase: "matches",
    done: 0,
    total: 0,
    label: "",
  });
  const [result, setResult] = useState(null);

  const start = async () => {
    setStatus("running");
    setProgress({ phase: "matches", done: 0, total: 0, label: "" });
    const res = await runMigration({
      firebaseUser,
      onProgress: (p) => setProgress(p),
    });
    setResult(res);
    setStatus(res.ok ? "done" : "error");
  };

  useEffect(() => {
    if (!firebaseUser?.uid || startedRef.current) return;
    if (!isAllowedEmailUser(firebaseUser)) return;
    startedRef.current = true;
    let cancelled = false;
    (async () => {
      const required = await needsMigration(firebaseUser.uid);
      if (cancelled || !required) return;
      start();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser]);

  // Auto-dismiss the success state after a short delay.
  useEffect(() => {
    if (status !== "done") return;
    const id = setTimeout(() => setStatus("idle"), 2500);
    return () => clearTimeout(id);
  }, [status]);

  if (status === "idle") return null;

  const overallPercent = (() => {
    if (status === "done") return 100;
    const base = PHASE_BASE[progress.phase] ?? 0;
    const weight = PHASE_WEIGHT[progress.phase] ?? 0;
    const frac =
      progress.total > 0 ? Math.min(1, progress.done / progress.total) : 0;
    return Math.round((base + weight * frac) * 100);
  })();

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-base-100 p-6 shadow-xl">
        {status === "running" && (
          <>
            <h3 className="text-lg font-semibold">
              {t("Setting up your account")}
            </h3>
            <p className="mt-1 text-sm opacity-70">
              {t("We're moving your data to a faster, more reliable home. This happens only once.")}
            </p>

            <div className="mt-4">
              <div className="flex items-center justify-between text-sm">
                <span>{t(PHASE_LABELS[progress.phase] ?? "Working")}</span>
                <span className="tabular-nums">{overallPercent}%</span>
              </div>
              <progress
                className="progress progress-primary mt-1 w-full"
                value={overallPercent}
                max="100"
              />
              {progress.total > 0 && (
                <div className="mt-1 text-xs opacity-60 tabular-nums">
                  {progress.done} / {progress.total}
                  {progress.label ? ` — ${progress.label}` : ""}
                </div>
              )}
            </div>
          </>
        )}

        {status === "done" && (
          <div className="text-center">
            <h3 className="text-lg font-semibold text-success">
              {t("All set!")}
            </h3>
            <p className="mt-1 text-sm opacity-70">
              {t("Your data has been migrated successfully.")}
            </p>
            {result?.counts && (
              <p className="mt-2 text-xs opacity-60">
                {result.counts.matches} {t("matches")} ·{" "}
                {result.counts.teams} {t("teams")} ·{" "}
                {result.counts.tournaments} {t("tournaments")}
              </p>
            )}
          </div>
        )}

        {status === "error" && (
          <>
            <h3 className="text-lg font-semibold text-error">
              {t("Migration finished with some issues")}
            </h3>
            <p className="mt-1 text-sm opacity-70">
              {t("Most of your data was migrated, but some items could not be moved. You can retry now or continue and try again later.")}
            </p>
            {result?.errors?.length > 0 && (
              <ul className="mt-3 max-h-32 overflow-auto rounded-lg bg-base-200 p-2 text-xs">
                {result.errors.slice(0, 8).map((e, i) => (
                  <li key={i} className="opacity-70">
                    • {e}
                  </li>
                ))}
                {result.errors.length > 8 && (
                  <li className="opacity-50">
                    {t("…and")} {result.errors.length - 8} {t("more")}
                  </li>
                )}
              </ul>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setStatus("idle")}
              >
                {t("Continue")}
              </button>
              <button className="btn btn-primary btn-sm" onClick={start}>
                {t("Retry")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
